import { useQuery } from "@tanstack/react-query";
import EditIcon from "../../../icons/EditIcon";
import EmailIcon from "../../../icons/EmailIcon";
import PlusIcon from "../../../icons/PlusIcon";
import Modal, { ModalProvider, ModalTrigger } from "../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../Table/TableBasic";
import MailskabelonModal from "./MailskabelonModal/MailskabelonModal";
import { get } from "../../../../api/util";
import Spinner from "../../../spinner/Spinner";
import { getHeaderActiveColumns } from "../../../../util/table";
import { useContext, useState } from "react";
import useActiveColumns from "../../../../hooks/useActiveColumns";
import { defaultMailskabelonColumns } from "../../../../config/admin/columnsConfig";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import { SortBy } from "../../../../interfaces/table";
import { IMailskabelonFields } from "../../../../interfaces/admin";
import { UserQueryContext } from "../../../../context/UserContext";
import { orderBy } from "../../../../util/orderBy";

export default function Mailskabeloner() {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "AdminWrite");
  const [sortBy, setSortBy] = useState<SortBy[]>(defaultSortBy);
  const [activeColumns, setActiveColumns] = useActiveColumns(
    `admin-mailskabeloner-active-columns`,
    defaultMailskabelonColumns,
  );
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["mailskabeloner"],
    queryFn: async function () {
      return get<MailskabelonDTO[]>({ query: "email/skabeloner" });
    },
  });
  return (
    <div className="container">
      <h3 className="d-flex gap-2 align-items-center">
        <span className="c-theme-icon c-theme-icon--defaultSize text-primary bg-primary-light">
          <EmailIcon height={16} />
        </span>
        <span>Mail-skabeloner</span>
      </h3>
      <ModalProvider>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <TableRoot className="table--clear-empty-columns">
              <TableHead>
                <>
                  {getHeaderActiveColumns(activeColumns, {
                    defaultSortBy,
                    setSortBy,
                    sortBy,
                  })}
                </>
                <th></th>
              </TableHead>
              <TableBody>
                {orderBy(
                  data ?? [],
                  sortBy.map((s) => s.field),
                  sortBy.map((s) => s.direction),
                ).map((s) => {
                  return (
                    <TableRow key={s.id}>
                      <>
                        {activeColumns
                          .filter((col) => col.active)
                          .map((col) => {
                            if (typeof s[col.field] === "object") {
                              return (
                                <td key={col.field}>{s[col.field]?.length}</td>
                              );
                            }
                            return (
                              <td key={col.field}>{s[col.field] as any}</td>
                            );
                          })}
                      </>
                      <td className="edit-column">
                        <div className="d-flex align-items-center justify-content-end gap-2">
                          <ModalProvider>
                            {s.id && canWrite && (
                              <ModalTrigger className="btn btn-primary btn--transparent has-icon px-2 tooltip-center justify-content-end">
                                <span className="icon-bg badge">
                                  <EditIcon />
                                </span>
                              </ModalTrigger>
                            )}
                            <Modal title="Redigér email-skabelon">
                              <MailskabelonModal
                                action="rediger"
                                data={s}
                                onSuccess={refetch}
                              />
                            </Modal>
                          </ModalProvider>
                        </div>
                      </td>
                    </TableRow>
                  );
                })}
              </TableBody>
            </TableRoot>
            <ColumnEditorModal
              defaultColumns={defaultMailskabelonColumns}
              columns={activeColumns}
              setActiveColumns={setActiveColumns}
            />
          </>
        )}
      </ModalProvider>
      <div className="col d-flex align-items-center justify-content-end gap-5">
        <ModalProvider>
          <ModalTrigger className="btn btn-primary btn--transparent has-icon px-2 tooltip-center justify-content-end">
            Opret ny
            <span className="icon-bg badge">
              <PlusIcon />
            </span>
          </ModalTrigger>
          <Modal title="Opret email-skabelon" description="">
            <MailskabelonModal action="opret" onSuccess={refetch} />
          </Modal>
        </ModalProvider>
      </div>
    </div>
  );
}

const defaultSortBy: SortBy<IMailskabelonFields>[] = [
  {
    direction: "asc",
    field: "navn",
  },
];
